import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Upload, Sparkles, CheckCircle2, AlertTriangle, X, RefreshCw, FileText, Save, HardHat, Link2,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Field, Input, Select, Spinner } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { fmtDate } from "@/lib/dates";
import { TICKET_TYPES } from "./SubbieHubScreens";

// =============================================================================
// SubbieWorkerBulkTicketsScreen — /subbies/:companyId/workers/:workerId/bulk
// =============================================================================
// James, 15 Sep 2026: "Same as their employee licence details, would be good to
// bulk upload and use the worker we built for our internal staff license
// reading and mapping."
//
// The subbie side of BulkLicenceIntakeScreen, cut down to what subbie_worker_
// tickets can hold: a free-text ticket_type, number, issue, expiry, one
// document. Same pipeline — upload three at a time (to ap-documents under
// vendor-compliance/worker-tickets/<worker>/, where the single "+ ticket" form
// already puts them), then /licences/ai-extract with bucket: "ap-documents".
// The AI's licence_type match becomes the ticket_type text; the reviewer can
// retype it (datalist of the usual names). Same holds as the staff screen:
// already loaded (same type + number), older than what's loaded, name on card
// isn't this worker — skipped unless "load anyway".
//
// 15 Sep 2026 (late) — James: "the bulk upload struggled to put the backs
// with fronts so maybe we have 2 lanes like employees". Same back lane as the
// staff screen now that mig 377 gives a ticket a back_document_path: a row the
// AI calls "back" attaches itself to the one front of the same type with no
// back yet (or the only front without a back when the type didn't match),
// else the reviewer picks the front from a list or says "It's a front". A file
// showing both sides (AI side "both", or the tick-box) points the back at the
// same file.
//
// Manager+ only (subbie_worker_tickets RLS is manager rw).
// =============================================================================

const CONCURRENCY = 3;
const MAX_BYTES   = 10 * 1024 * 1024;
const ACCEPT      = "image/jpeg,image/png,image/webp,application/pdf";

type RowStatus = "queued" | "uploading" | "reading" | "ready" | "back" | "error" | "saving" | "saved" | "save_error";

interface Row {
  id: string; file: File; previewUrl: string | null; storagePath: string | null;
  status: RowStatus; error: string | null;
  confidence: number | null; side: string | null; holderName: string | null; aiNote: string | null;
  licenceTypeId: string;          // the AI's licence_types match, kept only to pair backs with fronts
  ticketType: string; ticketNumber: string; issueDate: string; expiryDate: string;
  forceLoad: boolean;
  backOf: string | null;          // set on a BACK row: the front row it belongs to
  backPath: string | null;        // set on a FRONT row: the attached back's storage path
  bothSides: boolean;             // this one file shows front AND back
}

interface ExistingTicket { ticket_type: string; ticket_number: string | null; expiry_date: string | null }

const newId = () => (typeof crypto !== "undefined" && "randomUUID" in crypto) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const normNo = (v: string | null | undefined) => (v ?? "").replace(/[\s\-\/.]/g, "").toUpperCase();
const normType = (v: string | null | undefined) => (v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
function nameLooksLike(cardName: string | null, first: string | null, last: string | null): boolean | null {
  if (!cardName || (!first && !last)) return null;
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z ]/g, " ").split(/\s+/).filter(Boolean);
  const card = norm(cardName);
  const f = first ? norm(first)[0] : null;
  const l = last  ? norm(last).slice(-1)[0] : null;
  return (!f || card.includes(f)) && (!l || card.includes(l));
}

export default function SubbieWorkerBulkTicketsScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { companyId, workerId } = useParams<{ companyId: string; workerId: string }>();

  const [rows, setRows] = useState<Row[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState<{ saved: number; failed: number } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const rowsRef = useRef<Row[]>([]); rowsRef.current = rows;
  const inFlight = useRef(0);

  const { data: worker } = useQuery({
    queryKey: ["subbie_worker_bulk", workerId],
    enabled: !!workerId,
    queryFn: async () => {
      const { data, error } = await supabase.from("subbie_workers")
        .select("worker_id, first_name, last_name, job_title, subcontractor_company_id, subbie_worker_tickets(ticket_type, ticket_number, expiry_date)")
        .eq("worker_id", workerId!).maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });
  const { data: company } = useQuery({
    queryKey: ["subbie_worker_bulk_company", companyId],
    enabled: !!companyId,
    queryFn: async () => {
      const { data } = await supabase.from("subcontractor_companies").select("legal_name, trading_name").eq("subcontractor_company_id", companyId!).maybeSingle();
      return data as any;
    },
  });
  const existing: ExistingTicket[] = (worker?.subbie_worker_tickets ?? []) as ExistingTicket[];

  useEffect(() => () => { rowsRef.current.forEach(r => r.previewUrl && URL.revokeObjectURL(r.previewUrl)); }, []);

  const patch = (id: string, p: Partial<Row>) => setRows(prev => prev.map(r => (r.id === id ? { ...r, ...p } : r)));

  async function processRow(row: Row) {
    if (!workerId) return;
    try {
      patch(row.id, { status: "uploading", error: null });
      const safe = row.file.name.replace(/[^\w.\-]/g, "_");
      const path = row.storagePath ?? `vendor-compliance/worker-tickets/${workerId}/${Date.now()}-${safe}`;
      if (!row.storagePath) {
        const { error: upErr } = await supabase.storage.from("ap-documents").upload(path, row.file, { upsert: false });
        if (upErr) throw new Error(`Upload failed: ${upErr.message}`);
        patch(row.id, { storagePath: path });
      }
      patch(row.id, { status: "reading" });
      const res = await apiFetch("/licences/ai-extract", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ storage_path: path, bucket: "ap-documents" }),
      });
      if (!res.ok) { const txt = await res.text().catch(() => ""); throw new Error(`Couldn't read it (${res.status}): ${txt.slice(0, 300)}`); }
      const ai = await res.json() as {
        confidence: number; licence_type_id: string | null; licence_type_code_matched: string | null;
        extracted: { licence_number?: string | null; issue_date?: string | null; expiry_date?: string | null; notes?: string | null; side?: string | null; holder_name?: string | null };
      };
      // ticket_type is free text on subbie tickets: use the matched licence type's display name when we have one
      let typeName = "";
      if (ai.licence_type_id) {
        const { data: lt } = await supabase.from("licence_types").select("display_name").eq("licence_type_id", ai.licence_type_id).maybeSingle();
        typeName = (lt as any)?.display_name ?? "";
      }
      const side = ai.extracted?.side ?? "unknown";
      const isBack = side === "back";
      patch(row.id, {
        status: isBack ? "back" : "ready", bothSides: side === "both",
        confidence: ai.confidence ?? null, side,
        holderName: ai.extracted?.holder_name ?? null, aiNote: ai.extracted?.notes ?? null,
        licenceTypeId: ai.licence_type_id ?? "",
        ticketType: typeName, ticketNumber: ai.extracted?.licence_number ?? "",
        issueDate: ai.extracted?.issue_date ?? "", expiryDate: ai.extracted?.expiry_date ?? "",
      });
      // A back attaches itself when exactly one front of the same type has no
      // back yet; failing a type match, when exactly one front has no back at all.
      if (isBack) {
        const open = rowsRef.current.filter(r => r.id !== row.id && r.status === "ready" && !r.backPath && !r.bothSides);
        const sameType = ai.licence_type_id ? open.filter(r => r.licenceTypeId === ai.licence_type_id) : [];
        const pick = sameType.length === 1 ? sameType[0] : (sameType.length === 0 && open.length === 1 ? open[0] : null);
        if (pick) attachBack(row.id, pick.id, path);
      }
    } catch (e: any) {
      patch(row.id, { status: "error", error: e?.message ?? "Failed" });
    }
  }

  useEffect(() => {
    const queued = rows.filter(r => r.status === "queued");
    while (inFlight.current < CONCURRENCY && queued.length) {
      const next = queued.shift()!;
      inFlight.current += 1;
      patch(next.id, { status: "uploading" });
      processRow(next).finally(() => { inFlight.current -= 1; setRows(prev => [...prev]); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  function addFiles(list: FileList | File[]) {
    const accepted = ACCEPT.split(",");
    const fresh: Row[] = [];
    for (const f of Array.from(list)) {
      if (!accepted.includes(f.type) || f.size > MAX_BYTES) continue;
      fresh.push({ id: newId(), file: f, previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
        storagePath: null, status: "queued", error: null, confidence: null, side: null, holderName: null, aiNote: null,
        licenceTypeId: "", ticketType: "", ticketNumber: "", issueDate: "", expiryDate: "", forceLoad: false,
        backOf: null, backPath: null, bothSides: false });
    }
    if (fresh.length) { setSummary(null); setRows(prev => [...prev, ...fresh]); }
  }
  function attachBack(backRowId: string, frontRowId: string, backPath: string) {
    setRows(prev => prev.map(r => {
      if (r.id === backRowId)  return { ...r, backOf: frontRowId, status: "back" as RowStatus };
      if (r.id === frontRowId) return { ...r, backPath };
      if (r.backPath === backPath && r.id !== frontRowId) return { ...r, backPath: null }; // moved from another front
      return r;
    }));
  }
  function detachBack(backRowId: string) {
    setRows(prev => {
      const back = prev.find(r => r.id === backRowId);
      return prev.map(r => {
        if (r.id === backRowId) return { ...r, backOf: null };
        if (back && r.backPath === back.storagePath) return { ...r, backPath: null };
        return r;
      });
    });
  }
  function removeRow(id: string) {
    setRows(prev => {
      const gone = prev.find(r => r.id === id);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev
        .filter(r => r.id !== id)
        .map(r => (gone && r.backPath === gone.storagePath ? { ...r, backPath: null } : r))
        .map(r => (r.backOf === id ? { ...r, backOf: null } : r));
    });
  }
  function treatAsFront(id: string) {
    detachBack(id);
    patch(id, { status: "ready", side: "front" });
  }

  // ---- holds ---------------------------------------------------------------
  const existingOfType = (r: Row) => existing.filter(e => normType(e.ticket_type) === normType(r.ticketType));
  const holdReason = (r: Row): string | null => {
    if (r.forceLoad || !r.ticketType) return null;
    const same = existingOfType(r);
    const dupe = same.find(e => normNo(e.ticket_number) && normNo(e.ticket_number) === normNo(r.ticketNumber));
    if (dupe) return `already loaded${dupe.expiry_date ? ` (exp ${fmtDate(dupe.expiry_date)})` : ""}`;
    const newer = r.expiryDate ? same.find(e => e.expiry_date && e.expiry_date > r.expiryDate) : null;
    if (newer) return `older than what's loaded (exp ${fmtDate(newer.expiry_date!)})`;
    if (nameLooksLike(r.holderName, worker?.first_name ?? null, worker?.last_name ?? null) === false) return `name on card: ${r.holderName}`;
    return null;
  };
  const fronts = rows.filter(r => r.status !== "back" && r.backOf === null);
  const readyToSave = fronts.filter(r => (r.status === "ready" || r.status === "save_error") && r.ticketType.trim() && !holdReason(r));
  const heldBack = fronts.filter(r => r.status === "ready" && !!holdReason(r));
  const needType = fronts.filter(r => r.status === "ready" && !r.ticketType.trim());
  const unattachedBacks = rows.filter(r => r.status === "back" && !r.backOf);
  const busy = rows.some(r => ["queued", "uploading", "reading"].includes(r.status));

  async function saveAll() {
    if (!workerId || saving) return;
    setSaving(true);
    let saved = 0, failed = 0;
    for (const r of readyToSave) {
      patch(r.id, { status: "saving", error: null });
      const backRow = r.backPath ? rowsRef.current.find(b => b.storagePath === r.backPath) : null;
      const backPath = r.backPath ?? (r.bothSides ? r.storagePath : null);   // mig 377
      const { error } = await supabase.from("subbie_worker_tickets").insert({
        worker_id: workerId,
        ticket_type: r.ticketType.trim(),
        ticket_number: r.ticketNumber.trim() || null,
        issue_date: r.issueDate || null,
        expiry_date: r.expiryDate || null,
        document_path: r.storagePath, document_name: r.file.name,
        back_document_path: backPath,
        back_document_name: backPath ? (backRow?.file.name ?? r.file.name) : null,
      });
      if (error) { patch(r.id, { status: "save_error", error: error.message }); failed += 1; }
      else {
        patch(r.id, { status: "saved" }); saved += 1;
        if (backRow) patch(backRow.id, { status: "saved" });
      }
    }
    setSaving(false);
    setSummary({ saved, failed });
    qc.invalidateQueries({ queryKey: ["subbie_workers", companyId] });
    qc.invalidateQueries({ queryKey: ["subbie_worker_bulk", workerId] });
  }

  if (!user) return null;
  if (!hasRole(user, "manager")) {
    return <div className="p-6 max-w-2xl mx-auto"><Card><div className="p-5 text-[13px] text-danger">Subbie tickets are a manager action.</div></Card></div>;
  }
  const backLink = `/subbies/${companyId}`;
  const workerName = worker ? `${worker.first_name} ${worker.last_name}` : "";
  const doneCount = rows.filter(r => r.status === "saved").length;

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
      <Link to={backLink} className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"><ArrowLeft size={13} /> Back to {company?.trading_name || company?.legal_name || "subbie"}</Link>
      <div className="mb-4">
        <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2"><HardHat size={20} className="text-accent" /> Bulk add tickets{workerName ? ` for ${workerName}` : ""}</h1>
        <p className="text-text-muted text-[13px] mt-1">Drop the tickets the subbie sent for this worker. The AI reads each one, you check the table, then save the lot.</p>
      </div>

      <Card className="mb-4">
        <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
             onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }} onClick={() => fileInput.current?.click()}
             className={`m-4 rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${dragOver ? "border-accent bg-accent/5" : "border-line hover:border-line-light"}`}>
          <Upload size={22} className="mx-auto text-text-muted mb-2" />
          <div className="text-[14px] font-medium text-text">Drop files here or click to browse</div>
          <div className="text-[12px] text-text-muted mt-1">JPEG · PNG · WebP · PDF, up to 10 MB each. One file per ticket, or front + back as two files — backs pair up with their fronts.</div>
          <input ref={fileInput} type="file" multiple accept={ACCEPT} className="hidden" onChange={e => { if (e.target.files) addFiles(e.target.files); e.currentTarget.value = ""; }} />
        </div>
      </Card>

      {rows.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
          <Chip tone="muted">{rows.length} file{rows.length === 1 ? "" : "s"}</Chip>
          {busy && <Chip tone="info"><Spinner size={11} /> reading…</Chip>}
          <Chip tone="ok">{readyToSave.length} ready</Chip>
          {needType.length > 0 && <Chip tone="warn"><AlertTriangle size={11} /> {needType.length} need a type</Chip>}
          {heldBack.length > 0 && <Chip tone="warn">{heldBack.length} held back — see rows</Chip>}
          {unattachedBacks.length > 0 && <Chip tone="warn"><Link2 size={11} /> {unattachedBacks.length} back side{unattachedBacks.length === 1 ? "" : "s"} not attached</Chip>}
          {doneCount > 0 && <Chip tone="ok"><CheckCircle2 size={11} /> {doneCount} saved</Chip>}
        </div>
      )}

      <datalist id="subbie-ticket-types">{TICKET_TYPES.filter(t => !t.startsWith("Other")).map(t => <option key={t} value={t} />)}</datalist>

      <div className="space-y-3">
        {rows.map((r, idx) => {
          const isBackRow = r.status === "back" || (r.status === "saved" && r.backOf !== null);
          const hold = isBackRow ? null : holdReason(r);
          const lowConf = r.confidence != null && r.confidence < 0.6;
          const editable = r.status === "ready" || r.status === "save_error";
          const attachedBack = !isBackRow && r.backPath ? rows.find(b => b.storagePath === r.backPath) : null;
          return (
            <Card key={r.id} className={r.status === "saved" ? "border-ok/40" : (r.status === "error" || r.status === "save_error") ? "border-danger/40" : isBackRow ? "border-line bg-ink-raised/50" : ""}>
              <div className="p-3 md:p-4 flex gap-3 md:gap-4">
                <div className="shrink-0 w-[72px] h-[72px] md:w-[96px] md:h-[96px] rounded-md border border-line bg-ink-raised overflow-hidden flex items-center justify-center">
                  {r.previewUrl ? <img src={r.previewUrl} alt="" className="w-full h-full object-cover" /> : <FileText size={22} className="text-text-dim" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-[12px] font-mono text-text-muted truncate max-w-[42ch]" title={r.file.name}>{idx + 1}. {r.file.name}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <StatusChip status={r.status} />
                        {r.confidence != null && !isBackRow && <Chip tone={lowConf ? "warn" : "muted"}>{Math.round(r.confidence * 100)}% sure</Chip>}
                        {r.status === "ready" && !r.ticketType.trim() && <Chip tone="warn">type not recognised — type it in</Chip>}
                        {isBackRow && r.backOf && <Chip tone="ok"><Link2 size={11} /> back of {rows.findIndex(f => f.id === r.backOf) + 1}</Chip>}
                        {attachedBack && <Chip tone="ok"><Link2 size={11} /> back attached</Chip>}
                        {!isBackRow && r.bothSides && !r.backPath && <Chip tone="ok">both sides in this file</Chip>}
                        {hold && r.status === "ready" && <Chip tone="warn"><AlertTriangle size={11} /> {hold} — skipped</Chip>}
                        {r.forceLoad && r.status !== "saved" && <Chip tone="warn">loading anyway</Chip>}
                        {r.status === "ready" && (hold || r.forceLoad) && (
                          <button type="button" onClick={() => patch(r.id, { forceLoad: !r.forceLoad })} className="text-[11px] text-accent hover:underline">{r.forceLoad ? "skip it" : "load anyway"}</button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {r.status === "error" && <button type="button" onClick={() => patch(r.id, { status: "queued" })} className="p-1.5 rounded text-text-muted hover:text-text hover:bg-ink-hover" title="Read again"><RefreshCw size={14} /></button>}
                      {r.status !== "saving" && r.status !== "saved" && <button type="button" onClick={() => removeRow(r.id)} className="p-1.5 rounded text-text-muted hover:text-danger hover:bg-ink-hover" title="Remove"><X size={14} /></button>}
                    </div>
                  </div>
                  {r.error && <div className="mt-2 text-[12px] text-danger">{r.error}</div>}
                  {r.aiNote && !r.error && <div className="mt-2 text-[11px] text-text-muted italic">AI: {r.aiNote}</div>}
                  {/* BACK row: attach picker */}
                  {r.status === "back" && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                      <Field label="This is the back of">
                        <Select value={r.backOf ?? ""} onChange={e => {
                          const v = e.target.value;
                          if (!v) detachBack(r.id); else if (r.storagePath) attachBack(r.id, v, r.storagePath);
                        }}>
                          <option value="">— pick the front it belongs to —</option>
                          {fronts.filter(f => f.status !== "saved").map(f => (
                            <option key={f.id} value={f.id}>{rows.indexOf(f) + 1}. {f.ticketType.trim() || f.file.name}{f.backPath && f.backPath !== r.storagePath ? " (has a back)" : ""}</option>
                          ))}
                        </Select>
                      </Field>
                      <Button variant="secondary" size="sm" onClick={() => treatAsFront(r.id)} title="The AI got the side wrong — treat this as its own ticket">It's a front</Button>
                    </div>
                  )}
                  {!isBackRow && (editable || r.status === "saving" || r.status === "saved") && (
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
                      <div className="col-span-2">
                        <Field label="Ticket type"><Input list="subbie-ticket-types" value={r.ticketType} disabled={!editable} onChange={e => patch(r.id, { ticketType: e.target.value })} placeholder="e.g. Rigging advanced (RA)" /></Field>
                      </div>
                      <Field label="Number"><Input mono value={r.ticketNumber} disabled={!editable} onChange={e => patch(r.id, { ticketNumber: e.target.value })} /></Field>
                      <Field label="Issued"><Input type="date" value={r.issueDate} disabled={!editable} onChange={e => patch(r.id, { issueDate: e.target.value })} /></Field>
                      <Field label="Expiry"><Input type="date" value={r.expiryDate} disabled={!editable} onChange={e => patch(r.id, { expiryDate: e.target.value })} /></Field>
                      {!r.backPath && (
                        <label className="col-span-2 md:col-span-5 inline-flex items-center gap-2 text-[12px] text-text-muted cursor-pointer select-none">
                          <input type="checkbox" checked={r.bothSides} disabled={!editable} onChange={e => patch(r.id, { bothSides: e.target.checked })} className="accent-accent" />
                          This file shows both the front and the back of the card
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {rows.length === 0 && <div className="text-center text-[12px] text-text-muted py-6">Nothing here yet. One ticket at a time is still "+ ticket" on the subbie page.</div>}

      {rows.length > 0 && (
        <StickyBottomBar className="px-4 py-3">
          <div className="max-w-[1100px] mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[12px] text-text-muted">
              {summary
                ? <span><span className="text-ok font-medium">{summary.saved} saved</span>{summary.failed > 0 && <span> · <span className="text-danger font-medium">{summary.failed} failed</span> — fix and save again</span>}</span>
                : <span><span className="font-medium text-text">{readyToSave.length}</span> ready to save{needType.length > 0 && <span> · <span className="text-warn">{needType.length}</span> need a type (skipped)</span>}{heldBack.length > 0 && <span> · <span className="text-warn">{heldBack.length}</span> held back (skipped)</span>}{unattachedBacks.length > 0 && <span> · <span className="text-warn">{unattachedBacks.length}</span> back{unattachedBacks.length === 1 ? "" : "s"} unattached (ignored)</span>}</span>}
            </div>
            <div className="flex items-center gap-2">
              {summary && summary.failed === 0 && <Button variant="secondary" onClick={() => navigate(backLink)}>Done — back to subbie</Button>}
              <Button icon={Save} onClick={saveAll} disabled={saving || busy || readyToSave.length === 0}>{saving ? "Saving…" : `Save ${readyToSave.length} ticket${readyToSave.length === 1 ? "" : "s"}`}</Button>
            </div>
          </div>
        </StickyBottomBar>
      )}
    </div>
  );
}

function Chip({ tone, children }: { tone: "muted" | "ok" | "warn" | "info" | "danger"; children: ReactNode }) {
  const cls = { muted: "border-line text-text-muted bg-ink-raised", ok: "border-ok/30 text-ok bg-ok/5", warn: "border-warn/30 text-warn bg-warn/5", info: "border-info/30 text-info bg-info/5", danger: "border-danger/30 text-danger bg-danger/5" }[tone];
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>{children}</span>;
}
function StatusChip({ status }: { status: RowStatus }) {
  switch (status) {
    case "queued":     return <Chip tone="muted">queued</Chip>;
    case "uploading":  return <Chip tone="info"><Spinner size={11} /> uploading</Chip>;
    case "reading":    return <Chip tone="info"><Sparkles size={11} /> reading</Chip>;
    case "ready":      return <Chip tone="ok">read — check it</Chip>;
    case "back":       return <Chip tone="info">back side</Chip>;
    case "error":      return <Chip tone="danger">couldn't read</Chip>;
    case "saving":     return <Chip tone="info"><Spinner size={11} /> saving</Chip>;
    case "saved":      return <Chip tone="ok"><CheckCircle2 size={11} /> saved</Chip>;
    case "save_error": return <Chip tone="danger">save failed</Chip>;
  }
}
